import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../services/api';
import { usePoints } from '../context/PointsContext';
import ProfileAddressManager from '../components/ProfileAddressManager';
import './Profile.css';

interface UserProfile {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  avatar?: string;
  role: string;
  createdAt: string;
}

interface Order {
  _id: string;
  orderReference: string;
  total: number;
  status: string;
  createdAt: string;
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { deleteAccount } = useAuth();
  // Use points from context (this is the source of truth)
  const { points, pointsToCurrency, loading: pointsLoading, refreshPoints } = usePoints();
  
  console.log('Points from context in Profile:', points);

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedPhone, setEditedPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Define fetchAllData with useCallback to prevent unnecessary re-renders
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchProfile(),
        fetchOrders(),
        refreshPoints() // Use context's refreshPoints instead of local fetchPoints
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [refreshPoints]); // Add refreshPoints to dependency array

  // Now include fetchAllData in useEffect
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setProfile(data.user);
        setEditedName(data.user.name);
        setEditedPhone(data.user.phone || '');
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  };

  // Fetch orders from correct endpoint
  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/orders/my-orders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        let ordersList = [];
        if (data.orders && Array.isArray(data.orders)) {
          ordersList = data.orders;
        } else if (Array.isArray(data)) {
          ordersList = data;
        }
        setOrders(ordersList);
        console.log('✅ Orders fetched:', ordersList.length);
      } else {
        console.error('Failed to fetch orders:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: editedName,
          phone: editedPhone
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setProfile({ ...profile, name: editedName, phone: editedPhone } as UserProfile);
        setIsEditing(false);
        alert('Profile updated successfully!');
      } else {
        alert(data.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm('⚠️ WARNING: This action cannot be undone. All your data will be permanently deleted. Continue?')) {
      const confirmed = window.prompt('Type "DELETE" to confirm account deletion:');
      if (confirmed === 'DELETE') {
        setDeleting(true);
        const result = await deleteAccount();
        if (result.success) {
          alert('Your account has been deleted successfully.');
          navigate('/');
        } else {
          alert(result.error || 'Failed to delete account. Please try again.');
        }
        setDeleting(false);
      } else {
        alert('Account deletion cancelled.');
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading || pointsLoading) {
    return (
      <div className="profile-loading">
        <div className="spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-header">
        <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <h1>My Profile</h1>
      </div>

      <div className="profile-content">
        {/* Avatar Section */}
        <div className="avatar-section">
          <div className="avatar-circle">
            {profile?.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="avatar-info">
            <h2>{profile?.name}</h2>
            <p className="member-since">Member since {profile?.createdAt ? formatDate(profile.createdAt) : 'N/A'}</p>
          </div>
        </div>

        {/* Profile Info Section */}
        <div className="profile-card">
          <div className="card-header">
            <h3>Profile Information</h3>
            {!isEditing && (
              <button className="edit-btn" onClick={() => setIsEditing(true)}>
                ✎ Edit
              </button>
            )}
          </div>

          {isEditing ? (
            <div className="edit-form">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  placeholder="Enter your name"
                />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={editedPhone}
                  onChange={(e) => setEditedPhone(e.target.value)}
                  placeholder="Enter your phone number"
                />
              </div>
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={profile?.email || ''}
                  disabled
                  className="disabled-input"
                />
                <small>Email cannot be changed</small>
              </div>
              <div className="edit-actions">
                <button className="save-btn" onClick={handleSaveProfile} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button className="cancel-btn" onClick={() => setIsEditing(false)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="profile-details">
              <div className="detail-row">
                <span className="detail-label">Name:</span>
                <span className="detail-value">{profile?.name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Email:</span>
                <span className="detail-value">{profile?.email}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Phone:</span>
                <span className="detail-value">{profile?.phone || 'Not provided'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Account Type:</span>
                <span className="detail-value">{profile?.role === 'admin' ? 'Admin' : 'Regular User'}</span>
              </div>
            </div>
          )}
        </div>

        {/* Stats Section - WITH VIEW HISTORY BUTTONS */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">⭐</div>
            <div className="stat-content">
              <div className="stat-value">{points}</div>
              <div className="stat-label">Reward Points</div>
              <div className="stat-sub-label">≈ ₦{pointsToCurrency(points).toFixed(0)} value</div>
            </div>
            <button 
              className="view-history-btn"
              onClick={() => navigate('/points-history')}
            >
              View History →
            </button>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📦</div>
            <div className="stat-content">
              <div className="stat-value">{orders.length}</div>
              <div className="stat-label">My Orders</div>
            </div>
            <button 
              className="view-history-btn"
              onClick={() => navigate('/my-orders')}
            >
              View History →
            </button>
          </div>
        </div>
        
        <div className="profile-card">
          <ProfileAddressManager />
        </div>
        
        {/* Account Settings */}
        <div className="profile-card">
          <h3>Account Settings</h3>
          
          <div className="setting-item">
            <div className="setting-info">
              <span className="setting-icon">🔒</span>
              <div>
                <strong>Change Password</strong>
                <p>Update your password to keep your account secure</p>
              </div>
            </div>
            <button 
              className="setting-btn"
              onClick={() => navigate('/change-password')}
            >
              Change
            </button>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <span className="setting-icon">🔔</span>
              <div>
                <strong>Notifications</strong>
                <p>Manage your notification preferences</p>
              </div>
            </div>
            <button 
              className="setting-btn"
              onClick={() => navigate('/settings')}
            >
              Manage
            </button>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <span className="setting-icon">📄</span>
              <div>
                <strong>Privacy Policy</strong>
                <p>Read our privacy policy</p>
              </div>
            </div>
            <button 
              className="setting-btn"
              onClick={() => navigate('/privacy')}
            >
              View
            </button>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="danger-card">
          <h3>⚠️ Danger Zone</h3>
          <div className="danger-item">
            <div className="danger-info">
              <strong>Delete Account</strong>
              <p>Permanently delete your account and all data</p>
            </div>
            <button 
              className="danger-btn delete"
              onClick={handleDeleteAccount}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Account'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;